import { useEffect, useMemo, useState } from "react";
import { axiosWithToken, functionBaseUrl } from "../../../common/firebase";
import * as moment from "moment";

const DATA_TYPE_MAP = {
  subscriptions: {
    total: "allSubscriptions",
    active: "activeSubscriptions",
    canceled: "canceledSubscriptions",
  },
  users: { created: "usersCreated" },
  invites: { created: "invitesCreated" },
  sessions: { created: "sessionsCreated" },
  videos: { uploaded: "videosUploaded" },

  swingAndMiss: { total: "swingAndMissTotal", percentage: "swingAndMissPct" },
  analysisWithIssues: {
    total: "analysisWithIssuesTotal",
    percentage: "analysisWithIssuesPct",
  },
  confidenceScore: { bands: "confidenceBands", bandsPct: "confidenceBandsPct" },
};

const DEFAULT_DATA_TYPE = {
  subscriptions: "active",
  users: "created",
  invites: "created",
  sessions: "created",
  videos: "uploaded",

  swingAndMiss: "total",
  analysisWithIssues: "total",
  confidenceScore: "bands",
};

const RANGE_PRESETS = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "all", label: "All time" }, // no days
  { key: "custom", label: "Custom…" }, // uses customFrom/customTo
];

const REPORT_COLUMNS = {
  freeTrialUsers: [
    { header: "Name", keys: ["name", "fullName", "userName"] },
    { header: "Email", keys: ["userEmail"] },
    { header: "Platform", keys: ["platform", "platformName", "source"] },
    { header: "Plan", keys: ["plan", "planName", "subscriptionPlan"] },
    { header: "Start Date", keys: ["startDate", "trialStartDate"] },
    { header: "End Date", keys: ["endDate", "trialEndDate"] },
    {
      header: "Last Updated",
      keys: ["lastUpdated", "updatedAt", "updatedDate"],
    },
    { header: "Status", keys: ["status", "trialStatus"] },
    {
      header: "# of Students",
      keys: ["students", "studentCount", "numStudents", "studentsCount"],
    },
    {
      header: "# of Sessions",
      keys: ["sessions", "sessionCount", "numSessions", "sessionsCount"],
    },
    {
      header: "# of S&M",
      keys: ["issues"],
    },
  ],
  invites: [
    { header: "Invitee", keys: ["invitee"] },
    { header: "Invitee Name", keys: ["inviteeName"] },
    { header: "Sender", keys: ["senderName"] },
    { header: "Role", keys: ["role"] },
    { header: "Status", keys: ["status"] },
    { header: "Created Date", keys: ["createdAt"] },
    { header: "Expiry Date", keys: ["expiredIn"] },
  ],
  canceledSubs: [
    { header: "Name", keys: ["userName"] },
    { header: "Email", keys: ["userEmail"] },
    { header: "VIP Account", keys: ["vip"] },
    { header: "Platform", keys: ["platform"] },
    { header: "Plan", keys: ["productId"] },
    { header: "Auto Renewal", keys: ["autoRenewal"] },
    { header: "Start Date", keys: ["startDate"] },
    { header: "Last Updated", keys: ["lastUpdated"] },
    { header: "End Date", keys: ["endDate", "canceledDate"] },
  ],
};

function presetDays(rangeKey) {
  return RANGE_PRESETS.find((r) => r.key === rangeKey)?.days ?? 30;
}

function clampRange(fromMs, toMs) {
  if (fromMs == null || toMs == null) return { fromMs, toMs };
  return fromMs <= toMs ? { fromMs, toMs } : { fromMs: toMs, toMs: fromMs };
}

// --- NEW FN: date helpers moved outside hook (no per-render redefinition)
function startOfDayMs(dateStr) {
  if (!dateStr) return undefined;
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

// --- NEW FN: date helpers moved outside hook (no per-render redefinition)
function endOfDayMs(dateStr) {
  if (!dateStr) return undefined;
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

// --- NEW FN: auto aggregation picker (existing behavior, now explicit)
function autoAgg(fromMs, toMs, rangeKey) {
  if (rangeKey === "all") return "months";
  if (!fromMs || !toMs) return "days";
  const spanDays = Math.ceil((toMs - fromMs) / 86400000);
  if (spanDays <= 35) return "days";
  if (spanDays <= 210) return "weeks";
  return "months";
}

// --- NEW FN: manual override support for UI "Group by"
function resolveAgg({ aggKey, fromMs, toMs, rangeKey }) {
  return aggKey === "auto" ? autoAgg(fromMs, toMs, rangeKey) : aggKey;
}

export default function useSummaryPage() {
  const [tab, setTab] = useState("graph");

  const [dataset, setDataset] = useState("subscriptions");
  const [dataType, setDataType] = useState(DEFAULT_DATA_TYPE["subscriptions"]);
  const [rangeKey, setRangeKey] = useState("30d");

  // --- NEW: group-by selection for aggregation
  const [aggKey, setAggKey] = useState("auto"); // auto | days | weeks | months | years

  const [customFrom, setCustomFrom] = useState(""); // "YYYY-MM-DD"
  const [customTo, setCustomTo] = useState("");

  const [graphSeries, setGraphSeries] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState(null);

  const [cumulative, setCumulative] = useState(false);

  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [activeReportType, setActiveReportType] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewPage, setPreviewPage] = useState(1);
  const previewPageSize = 10;

  useEffect(() => {
    setDataType(DEFAULT_DATA_TYPE[dataset]);
  }, [dataset]);

  useEffect(() => {
    function isPercentMode(ds, dt) {
      if (ds === "swingAndMiss" || ds === "analysisWithIssues") {
        return dt === "percentage";
      }
      if (ds === "confidenceScore") {
        return dt === "bandsPct";
      }
      return false;
    }

    if (isPercentMode(dataset, dataType)) setCumulative(false);
  }, [dataset, dataType]);

  // --- NEW: request window now includes isReady for cleaner "custom" gating
  const requestWindow = useMemo(() => {
    const nowMs = Date.now();

    if (rangeKey === "custom") {
      const fromMsRaw = startOfDayMs(customFrom);
      const toMsRaw = endOfDayMs(customTo);

      if (fromMsRaw == null || toMsRaw == null) {
        return { fromMs: undefined, toMs: undefined, isReady: false };
      }

      const { fromMs, toMs } = clampRange(fromMsRaw, toMsRaw);
      return { fromMs, toMs, isReady: true };
    }

    if (rangeKey === "all") {
      return { fromMs: undefined, toMs: nowMs, isReady: true };
    }

    const days = presetDays(rangeKey);
    return { fromMs: nowMs - days * 86400000, toMs: nowMs, isReady: true };
  }, [rangeKey, customFrom, customTo]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setGraphLoading(true);
        setGraphError(null);

        const backendDataType = DATA_TYPE_MAP[dataset]?.[dataType];
        if (!backendDataType) {
          throw new Error(
            `Invalid dataType "${dataType}" for dataset "${dataset}"`,
          );
        }

        const { fromMs, toMs, isReady } = requestWindow;

        // --- NEW: single gate for custom readiness
        if (!isReady) {
          if (!cancelled) setGraphSeries([]);
          return;
        }

        const res = await axiosWithToken(
          functionBaseUrl + "/api/admin/summary-graphs",
          {
            method: "POST",
            data: {
              dataset,
              dataType: backendDataType,
              // --- NEW: allow manual aggregation override
              periodAggregation: resolveAgg({
                aggKey,
                fromMs,
                toMs,
                rangeKey,
              }),
              dateFilterFrom: fromMs,
              dateFilterTo: toMs,
            },
          },
        );

        if (!cancelled) {
          setGraphSeries(res.data.series || []);
        }
      } catch (err) {
        if (!cancelled) {
          setGraphError(err);
          setGraphSeries([]);
        }
      } finally {
        if (!cancelled) setGraphLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // --- NEW: aggKey included so changing "Group by" refetches graph
  }, [dataset, dataType, rangeKey, requestWindow, aggKey]);

  function formatDateMMDDYYYY(v) {
    if (v == null || v === "") return "";

    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n) && n > 0) return moment(n).format("MM/DD/YYYY");

    const m = moment(v);
    if (m.isValid()) return m.format("MM/DD/YYYY");

    return String(v);
  }

  function formatDateFields(rows, fields) {
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => {
      const out = { ...(r || {}) };
      for (const f of fields) {
        if (f in out) out[f] = formatDateMMDDYYYY(out[f]);
      }
      return out;
    });
  }

  function pickFirstValue(row, keys) {
    for (const key of keys) {
      if (row && row[key] != null && row[key] !== "") return row[key];
    }
    return "";
  }

  function normalizeReportRows(type, rows) {
    const columns = REPORT_COLUMNS[type];
    if (!columns) return rows;
    return rows.map((row) => {
      const out = {};
      for (const col of columns) {
        let value = pickFirstValue(row, col.keys);
        if (typeof value === "boolean") value = value ? "Yes" : "No";
        out[col.header] = value == null ? "" : value;
      }
      return out;
    });
  }

  function getReportHeaders(type) {
    const columns = REPORT_COLUMNS[type];
    return columns ? columns.map((c) => c.header) : null;
  }

  function buildReportDateRange() {
    const { fromMs, toMs, isReady } = requestWindow;

    // if custom, don’t request until both exist
    if (rangeKey === "custom") {
      if (!isReady) return null;
      const fixed = clampRange(fromMs, toMs);
      return { from: fixed.fromMs, to: fixed.toMs };
    }

    // for presets and all-time
    return {
      from: fromMs, // may be undefined for "all"
      to: toMs,
    };
  }

  async function fetchReportInvites() {
    try {
      setActiveReportType("invites");
      setReportLoading(true);
      setReportError(null);

      const dateRange = buildReportDateRange(); // can be null
      if (rangeKey === "custom" && !dateRange) return [];

      const res = await axiosWithToken(
        functionBaseUrl + "/api/admin/report/invites",
        {
          method: "POST",
          data: {
            createdAt: dateRange, // only if your backend supports it; otherwise remove
          },
        },
      );

      const rows = res.data?.rows || [];
      const formatted = formatDateFields(rows, [
        "createdAt",
        "expiredIn",
        "lastUpdated",
      ]);
      return normalizeReportRows("invites", formatted);
    } catch (err) {
      setReportError(err);
      return [];
    } finally {
      setReportLoading(false);
      setActiveReportType(null);
    }
  }

  function computeVip(sub) {
    const transactionId = sub?.transactionId ?? "";
    const coachId = sub?.coachId;
    const productId = sub?.productId ?? "";
    const bootcampUser = !!sub?.bootcampUser;

    return transactionId === "" &&
      !coachId &&
      !productId.includes("enterprise") &&
      !bootcampUser
      ? "Yes"
      : "No";
  }

  async function fetchReportCanceledSubs() {
    try {
      setActiveReportType("canceledSubs");
      setReportLoading(true);
      setReportError(null);

      const dateRange = buildReportDateRange();
      if (rangeKey === "custom" && !dateRange) return [];

      const res = await axiosWithToken(
        functionBaseUrl + "/api/admin/report/canceled-subscriptions",
        {
          method: "POST",
          data: {
            endDate: dateRange, // or lastUpdated/date field you decided in backend
          },
        },
      );

      const rowsRaw = res.data?.rows || [];

      // ✅ add VIP like old impl
      const rowsWithVip = rowsRaw.map((r) => ({
        ...r,
        vip: r?.vip ?? computeVip(r), // keep backend vip if it ever exists
      }));

      const formatted = formatDateFields(rowsWithVip, [
        "canceledDate",
        "endDate",
        "startDate",
        "lastUpdated",
      ]);

      return normalizeReportRows("canceledSubs", formatted);
    } catch (err) {
      setReportError(err);
      return [];
    } finally {
      setReportLoading(false);
      setActiveReportType(null);
    }
  }

  async function fetchReportFreeTrialUsers() {
    try {
      setActiveReportType("freeTrialUsers");
      setReportLoading(true);
      setReportError(null);

      const dateRange = buildReportDateRange();
      if (rangeKey === "custom" && !dateRange) return [];

      const res = await axiosWithToken(functionBaseUrl + "/api/freeTrials", {
        method: "GET",
      });

      const rows = res.data || [];
      const formatted = formatDateFields(rows, [
        "createdAt",
        "createdDate",
        "trialStartDate",
        "trialEndDate",
        "startDate",
        "endDate",
        "lastUpdated",
        "updatedAt",
      ]);
      return normalizeReportRows("freeTrialUsers", formatted);
    } catch (err) {
      setReportError(err);
      return [];
    } finally {
      setReportLoading(false);
      setActiveReportType(null);
    }
  }

  function toCsvValue(v) {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  function rowsToCols(rows) {
    return Array.from(
      rows.reduce((set, r) => {
        Object.keys(r || {}).forEach((k) => set.add(k));
        return set;
      }, new Set()),
    );
  }

  function rowsToCsv(rows, reportType) {
    const cols = getReportHeaders(reportType) ?? rowsToCols(rows);
    const header = cols.map(toCsvValue).join(",");
    const lines = rows.map((r) =>
      cols.map((c) => toCsvValue(r?.[c])).join(","),
    );
    return [header, ...lines].join("\n");
  }

  function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }

  async function handleReportClick(type, fetchFn, filenamePrefix) {
    const hasPreview = previewType === type && previewRows.length > 0;
    if (hasPreview) {
      const csv = rowsToCsv(previewRows, type);
      downloadTextFile(`${filenamePrefix}_${Date.now()}.csv`, csv);
      return;
    }

    const rows = await fetchFn();
    setPreviewType(type);
    setPreviewRows(Array.isArray(rows) ? rows : []);
    setPreviewPage(1);
  }

  const previewCols = useMemo(() => {
    const reportHeaders = getReportHeaders(previewType);
    return reportHeaders ?? rowsToCols(previewRows);
  }, [previewRows, previewType]);

  const previewTotalPages = useMemo(() => {
    const total = Math.ceil(previewRows.length / previewPageSize);
    return Math.max(total, 1);
  }, [previewRows.length, previewPageSize]);

  const previewPageRows = useMemo(() => {
    const start = (previewPage - 1) * previewPageSize;
    return previewRows.slice(start, start + previewPageSize);
  }, [previewRows, previewPage, previewPageSize]);

  return {
    tab,
    setTab,

    dataset,
    setDataset,

    dataType,
    setDataType,

    rangeKey,
    setRangeKey,

    // --- NEW: expose aggregation controls to UI
    aggKey,
    setAggKey,

    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,

    graphSeries,
    graphLoading,
    graphError,

    cumulative,
    setCumulative,

    reportLoading,
    reportError,
    activeReportType,
    fetchReportInvites,
    fetchReportCanceledSubs,
    fetchReportFreeTrialUsers,

    previewType,
    previewRows,
    previewCols,
    previewPage,
    setPreviewPage,
    previewPageSize,
    previewTotalPages,
    previewPageRows,
    handleReportClick,
  };
}
